import _, { isObject } from "lodash";
import React, { useEffect } from "react";
import { AutocompleteAddressField } from "../features/permits/components/AutocompleteAddressField.jsx";
import { useParams } from "react-router-dom";
import { Controller, useFormContext } from "react-hook-form";
import { useClient } from "../hooks/use-client";
import { useId } from "react-aria";

declare global {
  interface Window {
    google: any;
  }
}

interface IControlledGeocoder {
  defaultValue: any;
  control: any;
  name: any;
  readonly?: boolean;
  required?: boolean;
  onBlur?: () => void;
  setValue: any;
  id?: string;
}

//Google geocoding data
//input: defaultValue is google geocoding JSON which includes the address attribute
export const ControlledGeocoder = ({
  defaultValue,
  control,
  name,
  readonly,
  required,
  onBlur,
  setValue,
  id: inputId,
}: IControlledGeocoder) => {
  const { clearErrors } = useFormContext();
  const { site } = useParams<{ site: string }>();
  const id = inputId ?? useId();

  const client = useClient(site);
  const defaultCenter = _.get(client.query, "data.payload.data.attributes.default-center");
  const isReady = client.query.isSuccess;

  /**
   * If the value is not in the expected address format, return a stringified version of the value so that it can be displayed in the input field (instead of N/A)
   *
   * @param val value of this field in the default value
   */
  function formatAsAddressObject(val: unknown) {
    if (isObject(val) && ("address" in val || "short_address" in val)) return val; // If the address is in the expected format, return it

    const stringVal = String(val);
    return {
      address: stringVal,
      short_address: stringVal,
      result: stringVal,
    };
  }

  /**
   * Set the default value if it changes (this is necessary because the default value can be retrieved asynchronously)
   */
  useEffect(() => {
    setValue(name, formatAsAddressObject(defaultValue));
  }, [defaultValue]);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required && "This field is required",
        validate: { validateGeocoder: (value) => !required || value.result !== "" || "This field is required" },
      }}
      defaultValue={defaultValue}
      render={({ onChange, value }) => {
        // Looks for 'address' field, then 'short_address'. If both don't exist, render a blank address
        let addressValue: string | any =
          _.get(value || defaultValue, "address", "") || _.get(value || defaultValue, "short_address", "");
        if (typeof addressValue !== "string") addressValue = ""; // React crashes if it tries to render a JS object, so set to blank if this value is not a string
        if (readonly) {
          return addressValue ? <span>{addressValue}</span> : <span style={{ color: "gray" }}>N/A</span>;
        }
        return (
          <>
            {isReady && (
              <AutocompleteAddressField
                inputValue={addressValue || ""}
                placeholder='Start typing to search for an address'
                inputProps={{
                  "style": { height: 38, fontSize: 14 },
                  "aria-required": required,
                }}
                onInputChange={(value) => {
                  clearErrors(name);
                  setValue(name, {
                    address: value,
                    shortAddress: value,
                    result: value,
                  });
                }}
                options={{
                  types: ["geocode"],
                  input: addressValue || "",
                  bounds: {
                    east: defaultCenter?.lng,
                    west: defaultCenter?.lng,
                    north: defaultCenter?.lat,
                    south: defaultCenter?.lat,
                  },
                }}
                onPlaceSelected={(place) => {
                  // Matches https://github.com/GoGovApps/gomobile_flutter/blob/master/lib/models/request_location.dart#L61
                  onChange({
                    source: "google",
                    address: place.formatted_address,
                    shortAddress: place.name,
                    result: JSON.stringify(place),
                    coordinates: {
                      latitude: place.geometry?.location?.lat(),
                      longitude: place.geometry?.location?.lng(),
                    },
                  });
                  if (onBlur) {
                    onBlur();
                  }
                }}
                id={id}
              />
            )}
          </>
        );
      }}
    />
  );
};
